/* eslint-disable import/no-named-as-default-member */
import { call, put, takeLatest, takeEvery } from "redux-saga/effects";
import { normalize, schema } from "normalizr";

import { authorizedRequest, uploadFile, downloadFile, downloadXLSFile } from "../../utils/api";
import {
  mergeState,
  insertToState,
  removeFromState,
  insertProperty,
  insertAttribute,
  insertCategory,
  insertAttributeOnALoan,
  insertCategoryOnALoan,
  insertAttributeOnAMezzLoan,
  insertCategoryOnAMezzLoan,
  dealChangePrepaymentFieldsCategory,
  mergeFileToDeal,
  mergeFeatureImageToDeal,
  insertAttributeOnAdditionalLoan,
  insertCategoryOnAdditionalLoan,
  insertAdditionalLoanToModel
} from "../../utils/reducers";
import { API_DEALS } from "../../constants/apiRoutes";
import dealStatuses from "../../constants/status";
import { ToastSuccess, ToastError, ToastInfo } from "../../components/Global/Toasts/Toasty";
import { defaultReturn, makePayload, generateDefaultActions, subKeySaga, requestSaga, fillDictionary } from "../utils/helpers";

import { fetchAvps } from "./avp";

export const UPDATE_PROPERTY_ASSUMPTIONS = generateDefaultActions("UPDATE_PROPERTY_ASSUMPTIONS");
export const updatePropertyAssumptions = payload => makePayload(UPDATE_PROPERTY_ASSUMPTIONS.value, payload);
function* updatePropertyAssumptionsSaga(payload) {
  const url = "/updatePropertyAssumptions";
  yield requestSaga(UPDATE_PROPERTY_ASSUMPTIONS, url, "POST", payload);
}

export const KILL_DEAL = generateDefaultActions("KILL_DEAL");
export const killDeal = payload => makePayload(KILL_DEAL.value, payload);
function* killDealSaga(payload) {
  const url = "/deal/killDeal";
  yield requestSaga(KILL_DEAL, url, "POST", payload);
}

export const REVIVE_DEAL = generateDefaultActions("REVIVE_DEAL");
export const reviveDeal = payload => makePayload(REVIVE_DEAL.value, payload);
function* reviveDealSaga(payload) {
  const url = "/deal/reviveDeal";
  yield requestSaga(REVIVE_DEAL, url, "POST", payload);
}

export const EXPORT_WHAT_IF = generateDefaultActions("EXPORT_WHAT_IF");
export const exportWhatIf = payload => makePayload(EXPORT_WHAT_IF.value, payload);
function* exportWhatIfSaga(payload) {
  const url = "/exportWhatIf";
  yield call(downloadXLSFile, url, "POST", payload, "");
}

export const SET_DEAL_TO_REFINANCE = generateDefaultActions("SET_DEAL_TO_REFINANCE");
export const setDealToRefinance = payload => makePayload(SET_DEAL_TO_REFINANCE.value, payload);
function* setDealToRefinanceSaga(payload) {
  const callbackObject = payload.callbackObject;
  delete payload.callbackObject;
  const url = "/deal/setDealToRefinance";
  yield requestSaga(SET_DEAL_TO_REFINANCE, url, "POST", payload);
  if (callbackObject) {
    callbackObject.originalThis.callback();
  }
}

export const CANCEL_DEAL_REFINANCE = generateDefaultActions("CANCEL_DEAL_REFINANCE");
export const cancelDealRefinance = payload => makePayload(CANCEL_DEAL_REFINANCE.value, payload);
function* cancelDealRefinanceSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/cancelDealRefinance";
  yield requestSaga(CANCEL_DEAL_REFINANCE, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const CANCEL_ADD_SUPPLEMENTAL = generateDefaultActions("CANCEL_ADD_SUPPLEMENTAL");
export const cancelAddSupplemental = payload => makePayload(CANCEL_ADD_SUPPLEMENTAL.value, payload);
function* cancelDealSupplementalSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  yield requestSaga(CANCEL_ADD_SUPPLEMENTAL, "/deal/cancelAddSupplemental", "POST", payload);
  if (callback) {
    callback();
  }
}

export const CANCEL_MODIFY_LOANS = generateDefaultActions("CANCEL_MODIFY_LOANS");
export const cancelModifyLoans = dealId => makePayload(CANCEL_MODIFY_LOANS.value, { dealId });
function* cancelModifyLoansSaga(payload) {
  yield requestSaga(CANCEL_MODIFY_LOANS, `/deal/${payload.dealId}/cancelModifyLoans`, "POST", payload);
}

export const COMPLETE_LOAN_MODIFICATION = generateDefaultActions("COMPLETE_LOAN_MODIFICATION");
export const completeLoanModification = deal => makePayload(COMPLETE_LOAN_MODIFICATION.value, { deal });
function* completeLoanModificationSaga(payload) {
  yield requestSaga(COMPLETE_LOAN_MODIFICATION, "/deal/completeLoanModification", "POST", payload.deal);
}

export const GET_DEAL_WITH_LOANS_BEING_MODIFIED = generateDefaultActions("GET_DEAL_FOR_LOAN_MODIFICATION");
export const getDealWithLoansBeingModified = dealId => makePayload(GET_DEAL_WITH_LOANS_BEING_MODIFIED.value, { dealId });
function* getDealWithLoansBeingModifiedSaga(payload) {
  yield requestSaga(GET_DEAL_WITH_LOANS_BEING_MODIFIED, `/deal/${payload.dealId}/getDealWithLoansBeingModified`, "GET");
}

export const SAVE_LOAN_PROCEED_DEAL_ASSUMPTIONS = generateDefaultActions("SAVE_LOAN_PROCEED_DEAL_ASSUMPTIONS");
export const saveLoanProceedDealAssumptions = payload => makePayload(SAVE_LOAN_PROCEED_DEAL_ASSUMPTIONS.value, payload);
function* saveLoanProceedDealAssumptionsSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/saveLoanProceedDealAssumptions";
  yield requestSaga(SAVE_LOAN_PROCEED_DEAL_ASSUMPTIONS, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const GET_ALL_INFO_FOR_CLOSING_REFI_DEAL = generateDefaultActions("GET_ALL_INFO_FOR_CLOSING_REFI_DEAL");
export const getAllInfoForClosingRefiDeal = payload => makePayload(GET_ALL_INFO_FOR_CLOSING_REFI_DEAL.value, payload);
function* getAllInfoForClosingRefiDealSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/getAllInfoForClosingARefinanceDeal";
  yield requestSaga(GET_ALL_INFO_FOR_CLOSING_REFI_DEAL, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const CLOSE_REFI_DEAL = generateDefaultActions("CLOSE_REFI_DEAL");
export const closeRefiDeal = payload => makePayload(CLOSE_REFI_DEAL.value, payload);
function* closeRefiDealSaga(payload) {
  const callback = payload.callback;
  delete payload.callback;
  const url = "/deal/closeRefiDeal";
  yield requestSaga(CLOSE_REFI_DEAL, url, "POST", payload);
  if (callback) {
    callback();
  }
}

export const GET_DEAL_PROPERTY_PROJECTIONS = generateDefaultActions("GET_DEAL_PROPERTY_PROJECTIONS");
export const getDealPropertyProjections = payload => makePayload(GET_DEAL_PROPERTY_PROJECTIONS.value, payload);
function* getDealPropertyProjectionsSaga(payload) {
  const url = `/getDealPropertyProjections/${payload.dealId}`;
  const requestSubKeySaga = subKeySaga(payload.dealId);
  yield requestSubKeySaga(GET_DEAL_PROPERTY_PROJECTIONS, url);
}

export const ACK_REDIRECT = generateDefaultActions("ACK_REDIRECT");
export const acknowledgeRedirect = payload => makePayload(ACK_REDIRECT.value, payload);
function* acknowledgeRedirectSaga() {}

export const DEAL_AVP_ATTRIBUTE_ADD = "DEAL_AVP_ATTRIBUTE_ADD";
export const DEAL_AVP_CATEGORY_ADD = "DEAL_AVP_CATEGORY_ADD";
export const DEAL_ADDITIONAL_LOAN_ADD = "DEAL_ADDITIONAL_LOAN_ADD";

export const DEAL_ADDITIONAL_LOAN_AVP_ATTRIBUTE_ADD = "DEAL_ADDITIONAL_LOAN_AVP_ATTRIBUTE_ADD";

export const DEAL_ADDITIONAL_LOAN_AVP_CATEGORY_ADD = "DEAL_ADDITIONAL_LOAN_AVP_CATEGORY_ADD";

export const DEAL_LOAN_AVP_ATTRIBUTE_ADD = "DEAL_LOAN_AVP_ATTRIBUTE_ADD";
export const DEAL_LOAN_AVP_CATEGORY_ADD = "DEAL_LOAN_AVP_CATEGORY_ADD";

export const DEAL_MEZZLOAN_AVP_ATTRIBUTE_ADD = "DEAL_MEZZLOAN_AVP_ATTRIBUTE_ADD";
export const DEAL_MEZZLOAN_AVP_CATEGORY_ADD = "DEAL_MEZZLOAN_AVP_CATEGORY_ADD";

export const DEALS_NAMES_FETCH_REQUEST = "DEALS_NAMES_FETCH_REQUEST";
export const DEALS_NAMES_FETCH_SUCCESS = "DEALS_NAMES_FETCH_SUCCESS";

export const DEALS_FETCH_REQUEST_STATUS = "DEALS_FETCH_REQUEST_STATUS";
export const DEALS_FETCH_SUCCESS_STATUS = "DEALS_FETCH_SUCCESS_STATUS";
export const DEALS_FETCH_FAILURE_STATUS = "DEALS_FETCH_FAILURE_STATUS";

export const DEALS_FETCH_REQUEST_PORTFOLIO = "DEALS_FETCH_REQUEST_PORTFOLIO";
export const DEALS_FETCH_SUCCESS_PORTFOLIO = "DEALS_FETCH_SUCCESS_PORTFOLIO";
export const DEALS_FETCH_FAILURE_PORTFOLIO = "DEALS_FETCH_FAILURE_PORTFOLIO";

export const DEALS_FETCH_REQUEST = "DEALS_FETCH_REQUEST";
export const DEALS_FETCH_SUCCESS = "DEALS_FETCH_SUCCESS";
export const DEALS_FETCH_FAILURE = "DEALS_FETCH_FAILURE";

export const DEAL_FETCH_REQUEST = "DEAL_FETCH_REQUEST";
export const DEAL_FETCH_SUCCESS = "DEAL_FETCH_SUCCESS";
export const DEAL_FETCH_FAILURE = "DEAL_FETCH_FAILURE";

export const GET_PIPELINE_ACTIVE_DEAL_DETAILS = generateDefaultActions("GET_PIPELINE_ACTIVE_DEAL_DETAILS");
export const GET_DEAL_NAME = generateDefaultActions("GET_DEAL_NAME");
export const getDealName = payload => makePayload(GET_DEAL_NAME.value, payload);
function* getDealNameSaga(payload) {
  const url = `/getDealName/${payload.dealId}`;
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(GET_DEAL_NAME, url);
}
export const DEAL_CREATE_REQUEST = "DEAL_CREATE_REQUEST";
export const DEAL_CREATE_SUCCESS = "DEAL_CREATE_SUCCESS";
export const DEAL_CREATE_FAILURE = "DEAL_CREATE_FAILURE";
export const DEAL_CREATE_SUCCESS_CLEAR = "DEAL_CREATE_SUCCESS_CLEAR";

export const DEAL_UPDATE_REQUEST = "DEAL_UPDATE_REQUEST";
export const DEAL_UPDATE_SUCCESS = "DEAL_UPDATE_SUCCESS";
export const DEAL_UPDATE_FAILURE = "DEAL_UPDATE_FAILURE";

export const DEAL_DELETE_REQUEST = "DEAL_DELETE_REQUEST";
export const DEAL_DELETE_SUCCESS = "DEAL_DELETE_SUCCESS";
export const DEAL_DELETE_FAILURE = "DEAL_DELETE_FAILURE";

export const PROPERTY_CREATE = "PROPERTY_CREATE";
export const DEAL_PREPAYMENT_FIELDS = "DEAL_PREPAYMENT_FIELDS";

export const DEALS_FILE_UPLOAD_REQUEST = "DEALS_FILE_UPLOAD_REQUEST";
export const DEALS_FILE_UPLOAD_FAILURE = "DEALS_FILE_UPLOAD_FAILURE";
export const DEALS_FILE_UPLOAD_SUCCESS = "DEALS_FILE_UPLOAD_SUCCESS";

export const CLOSE_DEAL = generateDefaultActions("CLOSE_DEAL");
export const GET_WINNING_QUOTES = generateDefaultActions("GET_WINNING_QUOTES");
export const GET_DEAL_METRICS = generateDefaultActions("GET_DEAL_METRICS");
export const GET_DEAL_FILES = generateDefaultActions("GET_DEAL_FILES");
export const DELETE_DEAL_FILE = generateDefaultActions("DELETE_DEAL_FILE");
export const ADD_NOTE_TO_DEAL = generateDefaultActions("ADD_NOTE_TO_DEAL");
export const GET_DEAL_NOTES = generateDefaultActions("GET_DEAL_NOTES");
export const DELETE_DEAL_NOTES = generateDefaultActions("DELETE_DEAL_NOTES");

export const UPDATE_PIPELINE_DEAL = generateDefaultActions("UPDATE_PIPELINE_DEAL");
export const updatePipelineDeal = (updatePayload, refreshPayload) => makePayload(UPDATE_PIPELINE_DEAL.value, { updatePayload, refreshPayload });
function* updatePipelineDealSaga(payload) {
  yield* updateDealSaga({ payload: payload.updatePayload });
  yield* getPipelineActiveDealDetailsSaga(payload.refreshPayload);
}

export const UPDATE_PORTFOLIO_DEAL = generateDefaultActions("UPDATE_PORTFOLIO_DEAL");
export const updatePortfolioDeal = (updatePayload, refreshPayload) => makePayload(UPDATE_PORTFOLIO_DEAL.value, { updatePayload, refreshPayload });
function* updatePortfolioDealSaga(payload) {
  yield* updateDealSaga({ payload: payload.updatePayload });
  yield* fetchDealSaga({ payload: payload.refreshPayload.dealId });
}
export const DEALS_FEATURE_IMAGE_UPLOAD_REQUEST = "DEALS_FEATURE_IMAGE_UPLOAD_REQUEST";
export const DEALS_FEATURE_IMAGE_UPLOAD_FAILURE = "DEALS_FEATURE_IMAGE_UPLOAD_FAILURE";
export const DEALS_FEATURE_IMAGE_UPLOAD_SUCCESS = "DEALS_FEATURE_IMAGE_UPLOAD_SUCCESS";

export const DEAL_KEY = "deals";
export const DEAL_ENTITY = new schema.Entity(DEAL_KEY, {});
export const DEAL_SCHEMA = [DEAL_ENTITY];

export const DOWNLOAD_DEALS_BY_STATUS = generateDefaultActions("DOWNLOAD_DEALS_BY_STATUS");

export const CLEAR_RELOAD = generateDefaultActions("CLEAR_RELOAD");
export const clearReload = () => ({ type: CLEAR_RELOAD.value });
function* clearReloadSaga() {
  yield put({ type: CLEAR_RELOAD.success });
}

export const SELL_DEAL = generateDefaultActions("SELL_DEAL");
export const sellDeal = deal => ({ type: SELL_DEAL.value, deal });
function* sellDealSaga(payload) {
  yield call(authorizedRequest, `/deal/${payload.deal.id}/sell`, "POST", payload.deal);
  yield put({ type: SELL_DEAL.success });
}

export const GET_DEAL_TO_SELL = generateDefaultActions("GET_DEAL_TO_SELL");
export const getDealToSell = dealId => ({ type: GET_DEAL_TO_SELL.value, dealId });
function* getDealToSellSaga(payload) {
  const response = yield call(authorizedRequest, `/deals/${payload.dealId}/forSelling`, "GET");
  yield put({ type: GET_DEAL_TO_SELL.success, payload: response.result });
}

export const RECALCUALTE_DEAL_EXIT_DEBT_YIELD = generateDefaultActions("RECALCUALTE_DEAL_EXIT_DEBT_YIELD");
export const recalculateDealExitDebtYield = payload => makePayload(RECALCUALTE_DEAL_EXIT_DEBT_YIELD.value, payload);
function* recalculateDealExitDebtYieldSaga(payload) {
  const url = "/deal/recalculateDealExitDebtYield";
  yield requestSaga(RECALCUALTE_DEAL_EXIT_DEBT_YIELD, url, "POST", payload);
}

export const ADD_SUPPLEMENTAL_LOANS = generateDefaultActions("ADD_SUPPLEMENTAL_LOANS");
export const addSupplementalLoans = dealId => ({ type: ADD_SUPPLEMENTAL_LOANS.value, dealId });
function* addSupplementalLoansSaga(payload) {
  yield call(authorizedRequest, `/deal/${payload.dealId}/addSupplementalLoans`, "POST");
  yield put({ type: ADD_SUPPLEMENTAL_LOANS.success, dealId: payload.dealId });
}

export const MODIFY_LOANS = generateDefaultActions("MODIFY_LOANS");
export const modifyLoans = (dealId, loanIds) => ({ type: MODIFY_LOANS.value, dealId, loanIds });
function* modifyLoansSaga(payload) {
  yield call(authorizedRequest, `/deal/${payload.dealId}/modifyLoans`, "POST", payload.loanIds);
  yield put({ type: MODIFY_LOANS.success, dealId: payload.dealId });
}

export const CLEAR_WHAT_IF_INFO = generateDefaultActions("CLEAR_WHAT_IF_INFO");
export const clearWhatIfInfo = payload => makePayload(CLEAR_WHAT_IF_INFO.value, payload);
function* clearWhatIfInfoSaga(payload) {
  yield put({ type: CLEAR_WHAT_IF_INFO.success });
}

export const GET_WHAT_IF_INFO = generateDefaultActions("GET_WHAT_IF_INFO");
export const getWhatIfInfo = payload => makePayload(GET_WHAT_IF_INFO.value, payload);
function* getWhatIfInfoSaga(payload) {
  yield put({ type: CLEAR_WHAT_IF_INFO.success });
  const url = "/getWhatIfInfo";
  yield requestSaga(GET_WHAT_IF_INFO, url, "POST", payload);
}

// EXPORT_DEAL_PDF
export const DOWNLOAD_DEAL_PDF = {
  ...generateDefaultActions("DOWNLOAD_DEAL_PDF"),

  isGeneratingPdf: "DOWNLOAD_DEAL_PDF_SET_IS_DOWNLOADING",
  setIsDownloadingPdf(payload) {
    return {
      type: "DOWNLOAD_DEAL_PDF_SET_IS_DOWNLOADING",
      payload
    };
  }
};
export const downloadDealPdf = payload => makePayload(DOWNLOAD_DEAL_PDF.value, payload);
function* downloadDealPdfSaga(payload) {
  try {
    yield put(DOWNLOAD_DEAL_PDF.setIsDownloadingPdf({ isLoading: true }));
    yield call(downloadFile, "deal/exportPdf", "POST", payload);
    yield put(DOWNLOAD_DEAL_PDF.createSuccess(payload));
  } catch (error) {
    yield put(DOWNLOAD_DEAL_PDF.createFailure(error));
  } finally {
    yield put(DOWNLOAD_DEAL_PDF.setIsDownloadingPdf({ isLoading: false }));
  }
}

// DEAL_SET_PROPERTY
export const DEAL_SET_PROPERTY = generateDefaultActions("DEAL_SET_PROPERTY");
export const dealSetProperty = payload => makePayload(DEAL_SET_PROPERTY.value, payload);
function* dealSetPropertySaga(payload) {
  const url = `/deal/${payload.dealId}/property`;
  const { postUpdateCall } = payload;
  delete payload.postUpdateCall;
  yield put(DOWNLOAD_DEAL_PDF.setIsDownloadingPdf({ isLoading: true }));
  yield requestSaga(DEAL_SET_PROPERTY, url, "PUT", payload.property);
  yield put(DOWNLOAD_DEAL_PDF.setIsDownloadingPdf({ isLoading: false }));
  if (postUpdateCall) {
    yield postUpdateCall();
  }
}

const initialState = {
  deals: [],
  pipelineActiveDealDetails: {},
  dealsByStatus: [],
  dealNames: {},
  dealsNames: [],
  metricData: [],
  count: 0,
  order: [],
  dealFiles: [],
  dealNotes: [],
  additionalDealData: [],
  dealPrepaymentAnalysis: {},
  propertyAssumptionToken: 0,
  closeRefiModelInfo: null,
  redirectTo: null,
  dealPropertyProjections: [],
  whatIfInfo: null,
  isLoading: false
};

export default function dealReducer(state = initialState, action) {
  const { type, payload, subKey } = action;
  switch (type) {
    case DOWNLOAD_DEAL_PDF.isGeneratingPdf:
      return {
        ...state,
        ...payload
      };
    case DOWNLOAD_DEAL_PDF.success:
      ToastSuccess("Save successful");

      return state;

    case CLEAR_WHAT_IF_INFO.success:
      return defaultReturn(state, null, "whatIfInfo");
    case GET_WHAT_IF_INFO.success:
      ToastInfo("What If Analysis Complete");
      if (payload.message !== "") {
        ToastError(payload.message);
      } else {
        console.log("GET_WHAT_IF_INFO", payload.gridResults);
      }

      return defaultReturn(state, payload, "whatIfInfo");
    case GET_WHAT_IF_INFO.failure:
      if (payload.message === "") {
        ToastError("What-If Calculator Error");
      }

      return state;

    case EXPORT_WHAT_IF.success:
      ToastSuccess("Export Request Heard");

      return state;

    case DEAL_SET_PROPERTY.success:
      return defaultReturn(state, payload.result, "set deal property");
    case DEAL_SET_PROPERTY.failure:
      ToastError("Failed to update deal property");

      return state;

    case SELL_DEAL.success:
      return { ...state, reloadDeal: true };
    case CLEAR_RELOAD.success:
      return { ...state, reloadDeal: undefined };
    case GET_DEAL_TO_SELL.success:
      return { ...state, dealToSell: payload };
    case ADD_SUPPLEMENTAL_LOANS.success:
    case MODIFY_LOANS.success:
      ToastSuccess("Copy Created");

      return { ...state, redirectTo: `/pipeline/active/deals/${action.dealId}` };
    case GET_ALL_INFO_FOR_CLOSING_REFI_DEAL.success:
      return defaultReturn(state, payload.data, "closeRefiModelInfo");
    case DEALS_FETCH_SUCCESS:
      return {
        ...state,
        count: payload.count,
        deals: mergeState(state.deals, payload, DEAL_KEY),
        order: payload.order
      };
    case ACK_REDIRECT.value:
      return { ...state, redirectTo: null };
    case CLOSE_REFI_DEAL.success:
      return { ...state, redirectTo: `/portfolio/funds/free/deals/${payload.dealId}` };
    case SET_DEAL_TO_REFINANCE.success:
      return { ...state, redirectTo: `/pipeline/active/deals/${payload.dealId}` };
    case CANCEL_DEAL_REFINANCE.success:
      return { ...state, redirectTo: `/portfolio/funds/free/deals/${payload.dealId}` };
    case CANCEL_ADD_SUPPLEMENTAL.success:
      return { ...state, redirectTo: `/portfolio/funds/free/deals/${payload.dealId}` };
    case CANCEL_MODIFY_LOANS.success:
      return { ...state, redirectTo: `/portfolio/funds/free/deals/${payload.dealId}` };
    case COMPLETE_LOAN_MODIFICATION.success:
      if (payload.data.dealStatusId === dealStatuses.MODIFYING) {
        return { ...state, reloadDeal: true };
      }

      return { ...state, redirectTo: `/portfolio/funds/free/deals/${payload.data.id}` };
    case GET_DEAL_WITH_LOANS_BEING_MODIFIED.success:
      return { ...state, dealWithLoansBeingModified: payload.data };
    case UPDATE_PROPERTY_ASSUMPTIONS.success:
      return defaultReturn(state, 0, "propertyAssumptionToken");
    case DEALS_FETCH_SUCCESS_STATUS:
      // eslint-disable-next-line no-case-declarations
      return fillDictionary(state, payload, "dealsByStatus", payload.status);
    case DEALS_NAMES_FETCH_SUCCESS:
      return defaultReturn(state, payload.result, "dealsNames");
    case GET_DEAL_PROPERTY_PROJECTIONS.success:
      const consolidatedProjections = payload && payload.result && payload.result.consolidatedProjections ? payload.result.consolidatedProjections : {};

      return fillDictionary(state, consolidatedProjections, "dealPropertyProjections", subKey);
    case GET_PIPELINE_ACTIVE_DEAL_DETAILS.success:
      return fillDictionary(state, payload, "pipelineActiveDealDetails", subKey);
    case GET_PIPELINE_ACTIVE_DEAL_DETAILS.failure:
      payload.isAuthorized = false;

      return fillDictionary(state, payload, "pipelineActiveDealDetails", subKey);
    case GET_DEAL_NAME.success:
      return fillDictionary(state, payload.data, "dealNames", subKey);
    case DEALS_FILE_UPLOAD_SUCCESS:
      return mergeFileToDeal(state, payload, DEAL_KEY, action.dealId);

    case DEALS_FEATURE_IMAGE_UPLOAD_SUCCESS:
      return mergeFeatureImageToDeal(state, payload, DEAL_KEY, action.dealId);

    case DEAL_CREATE_SUCCESS:
      ToastSuccess("Save successful");

      return insertToState(state, "newDealId", payload.deal.id);

    case DEAL_CREATE_SUCCESS_CLEAR:
      return insertToState(state, "newDealId", null);

    case DEAL_UPDATE_SUCCESS:
      ToastSuccess("Save successful");

      return insertToState(state, payload.deal.id, payload.deal);
    case CLOSE_DEAL.success:
      return state;
    case GET_WINNING_QUOTES.success:
      return {
        ...state,
        winningQuotes: payload.data
      };
    case DEAL_FETCH_SUCCESS:
      return insertToState(state, payload.deal.id, payload.deal);

    case DEAL_FETCH_FAILURE:
      return insertToState(state, payload.deal.id, payload.deal);

    case DEAL_DELETE_SUCCESS:
      return removeFromState(state, payload.id);

    case DEAL_AVP_ATTRIBUTE_ADD:
      return insertAttribute(state, payload);

    case DEAL_AVP_CATEGORY_ADD:
      return insertCategory(state, payload);

    case DEAL_LOAN_AVP_ATTRIBUTE_ADD:
      return insertAttributeOnALoan(state, payload);

    case DEAL_ADDITIONAL_LOAN_AVP_ATTRIBUTE_ADD:
      return insertAttributeOnAdditionalLoan(state, payload);

    case DEAL_ADDITIONAL_LOAN_AVP_CATEGORY_ADD:
      return insertCategoryOnAdditionalLoan(state, payload);

    case DEAL_ADDITIONAL_LOAN_ADD:
      return insertAdditionalLoanToModel(state, payload);

    case DEAL_LOAN_AVP_CATEGORY_ADD:
      return insertCategoryOnALoan(state, payload);

    case DEAL_MEZZLOAN_AVP_ATTRIBUTE_ADD:
      return insertAttributeOnAMezzLoan(state, payload);

    case DEAL_MEZZLOAN_AVP_CATEGORY_ADD:
      return insertCategoryOnAMezzLoan(state, payload);

    case PROPERTY_CREATE:
      ToastSuccess("Save successful");

      return insertProperty(state, payload);

    case DEAL_PREPAYMENT_FIELDS:
      return dealChangePrepaymentFieldsCategory(state, payload);

    case GET_DEAL_METRICS.success:
      return defaultReturn(state, payload.data, "metricData");

    case DOWNLOAD_DEALS_BY_STATUS.failure:
      ToastError("Export Failed");

      return null;

    case GET_DEAL_FILES.success:
      return fillDictionary(state, payload.data, "dealFiles", subKey);

    case GET_DEAL_NOTES.success:
    case DELETE_DEAL_NOTES.success:
      return fillDictionary(state, payload.data, "dealNotes", subKey);

    case GET_ADDITIONAL_DEAL_DETAILS.success:
      return defaultReturn(state, payload.data, "additionalDealData");

    case DEAL_PREPAYMENT_ANALYSIS.success:
      return defaultReturn(state, payload, "dealPrepaymentAnalysis");

    case SAVE_LOAN_PROCEED_DEAL_ASSUMPTIONS.success:
      ToastSuccess("Save successful");

      return state;

    default:
      return state;
  }
}

/**
 * Get all deals
 * @return {Object} Action
 */
export function fetchDeals(payload) {
  return {
    type: DEALS_FETCH_REQUEST,
    ...payload
  };
}

export function dealsFetchSuccess({ deals, count }) {
  return {
    type: DEALS_FETCH_SUCCESS,
    payload: {
      data: normalize(deals, DEAL_SCHEMA).entities,
      count,
      order: deals.map(deal => deal.id)
    }
  };
}

export function dealsFetchSuccessStatus({ deals, count }, statusId) {
  return {
    type: DEALS_FETCH_SUCCESS_STATUS,
    payload: {
      data: normalize(deals, DEAL_SCHEMA).entities,
      count,
      order: deals.map(deal => deal.id),
      status: statusId
    }
  };
}

export function dealsNamesFetchSuccess(response) {
  return {
    type: DEALS_NAMES_FETCH_SUCCESS,
    payload: response
  };
}

function dealsFetchFailure(payload) {
  return {
    type: DEALS_FETCH_FAILURE,
    payload
  };
}

/**
 * Create a Deal
 * @return {Object} Action
 */
export function createDeal(
  payload,
  config = {
    redirect: true
  }
) {
  return {
    type: DEAL_CREATE_REQUEST,
    payload,
    ...config
  };
}

export function createDealSuccess(payload) {
  return {
    type: DEAL_CREATE_SUCCESS,
    payload
  };
}

export function clearNewDealId() {
  return {
    type: DEAL_CREATE_SUCCESS_CLEAR
  };
}

function createDealFailure(payload) {
  return {
    type: DEAL_CREATE_FAILURE,
    payload
  };
}

export const getPipelineActiveDealDetails = payload => makePayload(GET_PIPELINE_ACTIVE_DEAL_DETAILS.value, payload);
function* getPipelineActiveDealDetailsSaga(payload) {
  const url = `/getPipelineActiveDealDetails/${payload.dealId}`;
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(GET_PIPELINE_ACTIVE_DEAL_DETAILS, url);
}

export const downloadDeals = payload => makePayload(DOWNLOAD_DEALS_BY_STATUS.value, payload);

// getAdditionalDealDetailsByDealId
export const GET_ADDITIONAL_DEAL_DETAILS = generateDefaultActions("GET_ADDITIONAL_DEAL_DETAILS");
export const getAdditionalDealDetailsByDealId = payload => makePayload(GET_ADDITIONAL_DEAL_DETAILS.value, payload);
function* getAdditionalDealDetailsByDealIdSaga(payload) {
  const url = `/getAdditionalDealDetailsByDealId/${payload.dealId}`;
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(GET_ADDITIONAL_DEAL_DETAILS, url);
}

/**
 * Get a single deal
 * @return {Object} Action
 */
export function fetchDeal(payload) {
  return {
    type: DEAL_FETCH_REQUEST,
    payload
  };
}

export function dealFetchSuccess(payload) {
  return {
    type: DEAL_FETCH_SUCCESS,
    payload
  };
}

function dealFetchFailure(payload) {
  return {
    type: DEAL_FETCH_FAILURE,
    payload
  };
}

/**
 * Get a update deal
 * @return {Object} Action
 */
export function updateDeal(payload) {
  return {
    type: DEAL_UPDATE_REQUEST,
    payload
  };
}

export function closeDeal(payload) {
  return {
    type: CLOSE_DEAL.value,
    payload
  };
}

export function getWinningQuotes(payload) {
  return {
    type: GET_WINNING_QUOTES.value,
    payload
  };
}

export function dealAddAttribute(payload) {
  return {
    type: DEAL_AVP_ATTRIBUTE_ADD,
    payload
  };
}
export function dealAddCategory(payload) {
  return {
    type: DEAL_AVP_CATEGORY_ADD,
    payload
  };
}
export function dealAddPresetCategory(payload) {
  return {
    type: DEAL_PREPAYMENT_FIELDS,
    payload
  };
}

export function dealAddAttributeOnAAdditionalLoans(payload) {
  return {
    type: DEAL_ADDITIONAL_LOAN_AVP_ATTRIBUTE_ADD,
    payload
  };
}

export function addCategoryOnAdditionalLoan(payload) {
  return {
    type: DEAL_ADDITIONAL_LOAN_AVP_CATEGORY_ADD,
    payload
  };
}
export function addSupplementalLoanToDealModel(payload) {
  return {
    type: DEAL_ADDITIONAL_LOAN_ADD,
    payload
  };
}

export function dealAddAttributeOnALoan(payload) {
  return {
    type: DEAL_LOAN_AVP_ATTRIBUTE_ADD,
    payload
  };
}
export function dealAddCategoryOnALoan(payload) {
  return {
    type: DEAL_LOAN_AVP_CATEGORY_ADD,
    payload
  };
}

export function dealAddAttributeOnAMezzLoan(payload) {
  return {
    type: DEAL_MEZZLOAN_AVP_ATTRIBUTE_ADD,
    payload
  };
}
export function dealAddCategoryOnAMezzLoan(payload) {
  return {
    type: DEAL_MEZZLOAN_AVP_CATEGORY_ADD,
    payload
  };
}

export function dealUpdateSuccess(payload) {
  return {
    type: DEAL_UPDATE_SUCCESS,
    payload
  };
}

function dealUpdateFailure(payload) {
  Object.values(payload.response.data.errors.deal).map(item => ToastError(`${item}(449)`));

  return {
    type: DEAL_UPDATE_FAILURE,
    payload
  };
}

/**
 * Get a delete deal
 * @return {Object} Action
 */
export function deleteDeal(payload) {
  return {
    type: DEAL_DELETE_REQUEST,
    payload
  };
}

export function dealDeleteSuccess(payload) {
  return {
    type: DEAL_DELETE_SUCCESS,
    payload
  };
}

function dealDeleteFailure(payload) {
  return {
    type: DEAL_DELETE_FAILURE,
    payload
  };
}

export function createProperty(dealId, property) {
  return {
    type: PROPERTY_CREATE,
    payload: {
      dealId,
      property
    }
  };
}

export function addFileToDeal(model, modelId, file, url) {
  return {
    type: DEALS_FILE_UPLOAD_REQUEST,
    model,
    modelId,
    file,
    url
  };
}
export function addFileToDealSuccess(payload, dealId) {
  return {
    type: DEALS_FILE_UPLOAD_SUCCESS,
    payload,
    dealId
  };
}
export function addFileToDealFailure(payload) {
  return {
    type: DEALS_FILE_UPLOAD_FAILURE,
    payload
  };
}

// FEATURE_IMAGE
export function addFeatureImageToDeal(model, modelId, file, url, callback) {
  return {
    type: DEALS_FEATURE_IMAGE_UPLOAD_REQUEST,
    model,
    modelId,
    file,
    url,
    callback
  };
}
export function addFeatureImageToDealSuccess(payload, dealId) {
  return {
    type: DEALS_FEATURE_IMAGE_UPLOAD_SUCCESS,
    payload,
    dealId
  };
}
export function addFeatureImageToDealFailure(payload) {
  return {
    type: DEALS_FEATURE_IMAGE_UPLOAD_FAILURE,
    payload
  };
}

export const getDealMetrics = payload => makePayload(GET_DEAL_METRICS.value, payload);
export const getDealFiles = payload => makePayload(GET_DEAL_FILES.value, payload);
export const deleteDealFile = payload => makePayload(DELETE_DEAL_FILE.value, payload);
export const addNoteToDeal = (model, modelId, note) => ({
  type: ADD_NOTE_TO_DEAL.value,
  model,
  modelId,
  note,
  url: "/notes"
});
export const getDealNotes = payload => makePayload(GET_DEAL_NOTES.value, payload);
export const deleteNote = payload => makePayload(DELETE_DEAL_NOTES.value, payload);

// Saga FeatureImage
function* addFeatureImageToDealSaga(payload) {
  try {
    const callback = payload.callback;
    delete payload.callback;
    const response = yield call(uploadFile, payload);
    yield put(
      addFeatureImageToDealSuccess(
        {
          deal: response.deal
        },
        payload.modelId
      )
    );
    if (callback) {
      callback();
    }
  } catch (error) {
    yield put(addFeatureImageToDealFailure(error));
  }
}

// Saga
function* getDealFilesSaga(payload) {
  const url = "/getFiles/byModel";
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(GET_DEAL_FILES, url, "POST", payload);
}

// Saga
function* addFileToDealSaga(payload) {
  try {
    const response = yield call(uploadFile, payload);
    yield put(
      addFileToDealSuccess(
        {
          file: response.file
        },
        payload.modelId
      )
    );
    ToastSuccess(response.success === false ? response.msg : "File Uploaded");
    yield getDealFilesSaga({
      modelName: "deal",
      modelId: payload.modelId,
      subKey: payload.modelId
    });
  } catch (error) {
    yield put(addFileToDealFailure(error));
    ToastError(`${error.message}(595)`);
  }
}

// Saga
function* deleteDealFileSaga(payload) {
  const url = `/files/${payload.fileId}`;
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(DELETE_DEAL_FILE, url, "DELETE");
  if (payload.shouldUpdateAfter) {
    yield getDealFilesSaga({
      modelId: payload.modelId,
      modelName: payload.modelName,
      subKey: payload.subKey
    });
  }
}

// Saga
function* addNoteToDealSaga(payload) {
  const url = "/deal/addnote";
  const note = {
    model: "deal",
    modelId: payload.modelId,
    note: payload.note,
    pinned: false
  };
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(ADD_NOTE_TO_DEAL, url, "POST", {
    note
  });
  ToastSuccess("Note Added Successfully");
  yield getDealNotesSaga({
    modelName: "deal",
    modelId: payload.modelId,
    subKey: payload.modelId
  });
}

// Saga
function* getDealNotesSaga(payload) {
  const url = `/deals/notes/${payload.modelId}`;
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(GET_DEAL_NOTES, url, "GET");
}

// Saga
function* deleteNoteSaga(payload) {
  const { noteId, modelName, modelId } = payload;
  try {
    const url = `/notes/${noteId}?modelName=${modelName}&modelId=${modelId}`;
    const requestSubKeySaga = subKeySaga(payload.subKey);
    yield requestSubKeySaga(DELETE_DEAL_NOTES, url, "DELETE");
    ToastSuccess("Note Deleted");
  } catch (error) {
    ToastError(`Deleting Note Failed: ${error.message}`);
  }
}

// Saga
function* fetchDealsPaginatedSaga(payload) {
  try {
    let url = API_DEALS;
    const { page, limit, orderField, orderDir, screenType } = payload;
    // Add pagination params
    if (page !== null && limit !== null) {
      url = `${url}?page=${page + 1}&limit=${limit}`;
    }
    // Add sorting params
    if (orderField && orderDir) {
      url = `${url}&orderField=${orderField}&orderDir=${orderDir}`;
    }
    if (screenType) {
      url = `${url}&screenType=${screenType}`;
    }
    const response = yield call(authorizedRequest, url, "GET");
    yield put(dealsFetchSuccess(response));
  } catch (error) {
    yield put(dealsFetchFailure(error));
  }
}

export function fetchDealsStatus(statusId, payload) {
  return {
    type: DEALS_FETCH_REQUEST_STATUS,
    statusId,
    ...payload
  };
}

export function fetchDealsNames(payload) {
  return {
    type: DEALS_NAMES_FETCH_REQUEST,
    ...payload
  };
}

// Saga
function* fetchDealsPaginatedSagaByStatus(payload) {
  try {
    let url = `/deal/getDealsByStatusId?statusId=${payload.statusId}`;
    const { page, limit, orderField, orderDir, screenType } = payload;
    // Add pagination params
    if (page !== null && limit !== null) {
      url = `${url}&page=${page + 1}&limit=${limit}`;
    }
    // Add sorting params
    if (orderField && orderDir) {
      url = `${url}&orderField=${orderField}&orderDir=${orderDir}`;
    }
    if (screenType) {
      url = `${url}&screenType=${screenType}`;
    }

    const response = yield call(authorizedRequest, url, "GET");
    yield put(dealsFetchSuccessStatus(response, payload.statusId));
  } catch (error) {
    yield put(dealsFetchFailure(error));
  }
}

// Saga
function* fetchDealsNamesSaga() {
  try {
    const url = "/dealNames";
    const response = yield call(authorizedRequest, url, "GET");
    yield put(dealsNamesFetchSuccess(response));
  } catch (error) {
    yield put(dealsFetchFailure(error));
  }
}

// Saga
function* fetchDealsPaginatedSagaForPortfolio(payload) {
  try {
    let url = `${API_DEALS}/?statusId=${dealStatuses.ACTIVE}&statusId=${dealStatuses.LOAN_CLOSED}`;
    const { page, limit, orderField, orderDir } = payload;
    // Add pagination params
    if (page !== null && limit !== null) {
      url = `${url}&page=${page + 1}&limit=${limit}`;
    }
    // Add sorting params
    if (orderField && orderDir) {
      url = `${url}${url}&orderField=${orderField}&orderDir=${orderDir}`;
    }
    const response = yield call(authorizedRequest, url, "GET");
    yield put(dealsFetchSuccessStatus(response));
  } catch (error) {
    yield put(dealsFetchFailure(error));
  }
}

// Saga
function* createSaga(action) {
  const { payload } = action;
  try {
    const response = yield call(authorizedRequest, API_DEALS, "POST", payload);
    yield put(createDealSuccess(response));
  } catch (error) {
    yield put(createDealFailure(error));
  }
}

// Saga
function* fetchDealSaga(payload) {
  try {
    const response = yield call(authorizedRequest, `/deals/${payload.payload}`, "GET");
    if (response.deal) {
      yield put(
        dealFetchSuccess({
          deal: response.deal
        })
      );
      fetchAvps();
    } else {
      yield put(dealFetchFailure("Unauthorized"));
    }
  } catch (error) {
    const deal = { id: payload.payload, isAuthorized: false };
    yield put(dealFetchFailure({ deal }));
  }
}

// Saga

function* updateDealSaga(payload) {
  try {
    const deal = {
      deal: payload.payload
    };

    const response = yield call(authorizedRequest, `/deals/${payload.payload.id}`, "PUT", deal);
    yield put(dealUpdateSuccess(response));
  } catch (error) {
    yield put(dealUpdateFailure(error));
  }
}

function* closeDealSaga(payload) {
  try {
    const response = yield call(authorizedRequest, `/deal/${payload.payload}/close`, "POST");

    yield put({
      type: CLOSE_DEAL.success,
      payload: response
    });
  } catch (error) {
    yield put({
      type: CLOSE_DEAL.failure,
      payload: error
    });
  }
}

function* getWinningQuotesSaga(payload) {
  try {
    const response = yield call(authorizedRequest, `/getWinningQuotes/${payload.payload}`, "GET");

    yield put({
      type: GET_WINNING_QUOTES.success,
      payload: response
    });
  } catch (error) {
    yield put({
      type: GET_WINNING_QUOTES.failure,
      payload: error
    });
  }
}

// Saga
function* deleteDealSaga(payload) {
  try {
    const response = yield call(authorizedRequest, `/deals/${payload.payload.id}`, "DELETE");
    yield put(dealDeleteSuccess(response));
  } catch (error) {
    yield put(dealDeleteFailure(error));
    ToastError(`${error.message}(813)`);
  }
}

function* getDealMetricsSaga(payload) {
  const url = `/metrics/deals/${payload.dealId}`;
  const requestSubKeySaga = subKeySaga(payload.subKey);
  yield requestSubKeySaga(GET_DEAL_METRICS, url, "GET");
}

function* downloadDealsByStatusSaga(payload) {
  try {
    let url = `/deal/downloadDealsByStatusId?statusId=${payload.statusId}`;
    if (payload.fundId) {
      url = `${url}&fundId=${payload.fundId}`;
    }
    url = `${url}&shouldDownload=true`;
    const { page, limit, orderField, orderDir, screenType } = payload;
    // Add pagination params
    if (page !== null && limit !== null) {
      url = `${url}&page=${page + 1}&limit=${limit}`;
    }

    // Add sorting params
    if (orderField && orderDir) {
      url = `${url}&orderField=${orderField}&orderDir=${orderDir}`;
    }

    if (screenType) {
      url = `${url}&screenType=${screenType}`;
    }

    yield call(downloadFile, url, "GET");
  } catch (error) {
    yield put(DOWNLOAD_DEALS_BY_STATUS.createFailure(error));
  }
}

export const DEAL_PREPAYMENT_ANALYSIS = generateDefaultActions("DEAL_PREPAYMENT_ANALYSIS");
export const getDealPrepaymentAnalysis = payload => makePayload(DEAL_PREPAYMENT_ANALYSIS.value, payload);
function* getDealPrepaymentAnalysisSaga(payload) {
  const url = `/servermetrics/dealPrepaymentAnalysis?dealId=${payload.dealId}&prepaymentDate=${payload.prepaymentDate}`;
  yield requestSaga(DEAL_PREPAYMENT_ANALYSIS, url, "GET");
}

export const EXPORT_DEAL_PREPAYMENT_RESULTS = generateDefaultActions("EXPORT_DEAL_PREPAYMENT_RESULTS");
export const exportDealPrepaymentResults = payload => makePayload(EXPORT_DEAL_PREPAYMENT_RESULTS.value, payload);
function* exportDealPrepaymentResultsSaga(payload) {
  const url = "exportDealPrepaymentResults";
  yield call(downloadXLSFile, url, "POST", payload, "");
}

export function* dealSaga() {
  yield takeEvery(CLEAR_RELOAD.value, clearReloadSaga);
  yield takeEvery(SELL_DEAL.value, sellDealSaga);
  yield takeEvery(GET_DEAL_TO_SELL.value, getDealToSellSaga);
  yield takeEvery(ADD_SUPPLEMENTAL_LOANS.value, addSupplementalLoansSaga);
  yield takeEvery(MODIFY_LOANS.value, modifyLoansSaga);
  yield takeLatest(DEALS_FETCH_REQUEST, fetchDealsPaginatedSaga);
  yield takeEvery(DEALS_FETCH_REQUEST_STATUS, fetchDealsPaginatedSagaByStatus);
  yield takeLatest(DEALS_NAMES_FETCH_REQUEST, fetchDealsNamesSaga);
  yield takeLatest(DEALS_FETCH_REQUEST_PORTFOLIO, fetchDealsPaginatedSagaForPortfolio);
  yield takeLatest(DEALS_FILE_UPLOAD_REQUEST, addFileToDealSaga);
  yield takeLatest(GET_PIPELINE_ACTIVE_DEAL_DETAILS.value, getPipelineActiveDealDetailsSaga);
  yield takeLatest(GET_DEAL_NAME.value, getDealNameSaga);
  yield takeLatest(DEALS_FEATURE_IMAGE_UPLOAD_REQUEST, addFeatureImageToDealSaga);
  yield takeLatest(DEAL_CREATE_REQUEST, createSaga);
  yield takeLatest(DEAL_FETCH_REQUEST, fetchDealSaga);
  yield takeLatest(DEAL_UPDATE_REQUEST, updateDealSaga);
  yield takeLatest(CLOSE_DEAL.value, closeDealSaga);
  yield takeLatest(GET_WINNING_QUOTES.value, getWinningQuotesSaga);
  yield takeLatest(UPDATE_PIPELINE_DEAL.value, updatePipelineDealSaga);
  yield takeLatest(UPDATE_PORTFOLIO_DEAL.value, updatePortfolioDealSaga);
  yield takeLatest(DEAL_DELETE_REQUEST, deleteDealSaga);
  yield takeLatest(GET_DEAL_METRICS.value, getDealMetricsSaga);
  yield takeLatest(DOWNLOAD_DEALS_BY_STATUS.value, downloadDealsByStatusSaga);
  yield takeEvery(GET_DEAL_FILES.value, getDealFilesSaga);
  yield takeEvery(DELETE_DEAL_FILE.value, deleteDealFileSaga);
  yield takeLatest(ADD_NOTE_TO_DEAL.value, addNoteToDealSaga);
  yield takeLatest(GET_DEAL_NOTES.value, getDealNotesSaga);
  yield takeLatest(DELETE_DEAL_NOTES.value, deleteNoteSaga);
  yield takeLatest(GET_ADDITIONAL_DEAL_DETAILS.value, getAdditionalDealDetailsByDealIdSaga);
  yield takeLatest(DEAL_PREPAYMENT_ANALYSIS.value, getDealPrepaymentAnalysisSaga);
  yield takeLatest(EXPORT_DEAL_PREPAYMENT_RESULTS.value, exportDealPrepaymentResultsSaga);
  yield takeLatest(UPDATE_PROPERTY_ASSUMPTIONS.value, updatePropertyAssumptionsSaga);
  yield takeLatest(SET_DEAL_TO_REFINANCE.value, setDealToRefinanceSaga);
  yield takeLatest(CANCEL_DEAL_REFINANCE.value, cancelDealRefinanceSaga);
  yield takeLatest(CANCEL_ADD_SUPPLEMENTAL.value, cancelDealSupplementalSaga);
  yield takeLatest(CANCEL_MODIFY_LOANS.value, cancelModifyLoansSaga);
  yield takeLatest(COMPLETE_LOAN_MODIFICATION.value, completeLoanModificationSaga);
  yield takeLatest(GET_DEAL_WITH_LOANS_BEING_MODIFIED.value, getDealWithLoansBeingModifiedSaga);
  yield takeLatest(GET_ALL_INFO_FOR_CLOSING_REFI_DEAL.value, getAllInfoForClosingRefiDealSaga);
  yield takeLatest(CLOSE_REFI_DEAL.value, closeRefiDealSaga);
  yield takeLatest(ACK_REDIRECT.value, acknowledgeRedirectSaga);
  yield takeLatest(KILL_DEAL.value, killDealSaga);
  yield takeLatest(REVIVE_DEAL.value, reviveDealSaga);
  yield takeEvery(RECALCUALTE_DEAL_EXIT_DEBT_YIELD.value, recalculateDealExitDebtYieldSaga);
  yield takeLatest(EXPORT_WHAT_IF.value, exportWhatIfSaga);
  yield takeLatest(SAVE_LOAN_PROCEED_DEAL_ASSUMPTIONS.value, saveLoanProceedDealAssumptionsSaga);
  yield takeLatest(GET_DEAL_PROPERTY_PROJECTIONS.value, getDealPropertyProjectionsSaga);
  yield takeLatest(GET_WHAT_IF_INFO.value, getWhatIfInfoSaga);
  yield takeLatest(DOWNLOAD_DEAL_PDF.value, downloadDealPdfSaga);
  yield takeLatest(DEAL_SET_PROPERTY.value, dealSetPropertySaga);
  yield takeLatest(CLEAR_WHAT_IF_INFO.value, clearWhatIfInfoSaga);
}
